import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../components/shared/components/confirmation-dialog/confirmation-dialog.component';
import { PlannedRideService } from './planned-ride.service';

@Injectable({
    providedIn: 'root',
})
export class ConfirmationDialogService {
    private maxpoints = this.plannedRideService.MAX_POINTS;

    constructor(
        private matDialog: MatDialog,
        private plannedRideService: PlannedRideService
    ) {}

    /**
     * @description Open a ConfirmationDialogComponent instance with the
     * default values for confirming discarding changes.
     * @param id The value used to concat confirmButtonId and cancelButtonId.
     * @returns Gets an observable that is notified when the dialog is
     * finished closing.
     */
    openDiscardConfirmationDialog(id = '') {
        const confirm = this.matDialog.open(ConfirmationDialogComponent);
        confirm.componentInstance.dialogId = 'saveChanges';
        confirm.componentInstance.titleText = $localize`Unsaved Changes`;
        confirm.componentInstance.contentText = $localize`Discard Unsaved Changes?`;
        confirm.componentInstance.confirmText = $localize`Discard Changes`;
        confirm.componentInstance.cancelText = $localize`Keep Editing`;
        confirm.componentInstance.confirmButtonId = `discard${id}Changes`;
        confirm.componentInstance.cancelButtonId = `keepEditing${id}`;

        return confirm.afterClosed();
    }

    /**
     * @description Open a ConfirmationDialogComponent instance with the
     * default values for saving discarding cancel changes.
     * @param id The value used to concat confirmButtonId, centerButtonId and cancelButtonId.
     * @param contentText The value used in the body of the dialog.
     * @returns Gets an observable that is notified when the dialog is
     * finished closing.
     */
    openSaveDiscardCancelConfirmationDialog(id = '', contentText = '') {
        const confirm = this.matDialog.open(ConfirmationDialogComponent);
        confirm.componentInstance.dialogId = 'saveDiscardCancelDialog';
        confirm.componentInstance.titleText = $localize`Unsaved Changes`;
        confirm.componentInstance.contentText = contentText;
        confirm.componentInstance.confirmText = $localize`Save`;
        confirm.componentInstance.hideCenterButton = false;
        confirm.componentInstance.confirmButtonId = `save${id}Changes`;
        confirm.componentInstance.centerButtonId = `discard${id}Changes`;
        confirm.componentInstance.cancelButtonId = `cancel${id}`;

        return confirm.afterClosed();
    }

    openDismissDiagnosticCodeConfirmationDialog(id = '') {
        const confirm = this.matDialog.open(ConfirmationDialogComponent);
        confirm.componentInstance.dialogId = 'dismissAllCodes';
        confirm.componentInstance.titleText = $localize`Dismiss All Codes`;
        confirm.componentInstance.contentText = $localize`Are you sure you want to dismiss all codes?`;
        confirm.componentInstance.confirmText = $localize`Dismiss All Codes`;
        confirm.componentInstance.cancelText = $localize`Cancel`;
        confirm.componentInstance.confirmButtonId = `confirmDiscard${id}`;
        confirm.componentInstance.cancelButtonId = `cancelDiscard${id}`;

        return confirm.afterClosed();
    }

    /**
     * @description Open a ConfirmationDialogComponent instance with the
     * default values for confirming deleting changes.
     * @param id The value used to concat dialogId, confirmButtonId and
     * cancelButtonId. This value is used in titleText if no titleText is
     * supplied.
     * @param contentText The content of the confirmation dialog box.
     * @param titleText (optional) override title text.
     * @param confirmText (optional) override confirm button text.
     * @returns Gets an observable that is notified when the dialog is
     * finished closing.
     */
    openDeleteConfirmationDialog(
        id = '',
        contentText = '',
        titleText?: string,
        confirmText?: string
    ) {
        const confirm = this.matDialog.open(ConfirmationDialogComponent);
        confirm.componentInstance.dialogId = `delete${id}`;
        confirm.componentInstance.titleText = titleText
            ? titleText
            : $localize`Delete ${id}?`;
        confirm.componentInstance.contentText = contentText;
        confirm.componentInstance.confirmText = confirmText
            ? confirmText
            : $localize`Delete`;
        confirm.componentInstance.cancelText = $localize`Cancel`;
        confirm.componentInstance.confirmButtonId = `confirmDelete${id}`;
        confirm.componentInstance.cancelButtonId = `cancelDelete${id}`;

        return confirm.afterClosed();
    }

    openPlannedRideMaxPointMessage() {
        const confirm = this.matDialog.open(ConfirmationDialogComponent);
        confirm.componentInstance.dialogId = 'maxPointsReached';
        confirm.componentInstance.titleText = $localize`Route Limit Reached`;
        const contentText = $localize`The maximum of ${this.maxpoints} stops and route adjustments has been reached. Plan the rest of your ride in another route.`;
        confirm.componentInstance.contentText = contentText;
        confirm.componentInstance.confirmText = $localize`OK`;
        confirm.componentInstance.confirmButtonId =
            'confirmMaxPlannedRidePointsReached';
        confirm.componentInstance.hideCancelButton = true;

        return confirm.afterClosed();
    }
}
